/* import __COLOCATED_TEMPLATE__ from './line-provisioning.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import {
  type Provider,
  type IncomingType,
  type ProvisioningStep,
  PROVISIONING_STEPS,
  PROVIDERS,
  INCOMING_TYPES,
} from './constants';
import type IntlService from 'embercom/services/intl';
import type CallingPhoneNumber from 'embercom/models/calling-phone-number';
import { PhoneNumberProvider } from 'embercom/models/calling-phone-number';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import { PhoneNumberType, PHONE_NUMBER_COUNTRY_DETAILS } from 'embercom/models/settings/calling';
import { get, post } from 'embercom/lib/ajax';
import { type TaskGenerator } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type Owner from '@ember/owner';

interface PhonePricingResponse {
  price: number;
  currency?: string;
  recurring?: boolean;
}

interface NotificationsService {
  notifyError(message: string): void;
  notifyConfirmation(message: string): void;
}

interface Store {
  pushPayload(payload: Record<string, any>): void;
  peekRecord(modelName: string, id: string): any;
}

interface Args {
  showModal: boolean;
  onCloseModal: () => void;
  openHelpArticle: () => void;
  onLineCreated?: (phoneNumberId: string) => void;
}

interface Signature {
  Args: Args;
}

export default class LineProvisioning extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: NotificationsService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked provider: Provider = PROVIDERS.INTERCOM;
  @tracked lineName = '';
  @tracked incomingType: IncomingType = INCOMING_TYPES.PSTN;
  @tracked step: ProvisioningStep = PROVISIONING_STEPS.PROVIDER_SELECTION;
  @tracked isDirty = false;
  @tracked showConfirmationModal = false;
  @tracked isLoading = false;
  @tracked selectedPhoneNumber = '';
  @tracked selectedPhoneNumberPrice = 0;
  @tracked availablePhoneNumbers: string[] = [];
  @tracked thirdPartyProvider: PhoneNumberProvider = PhoneNumberProvider.Other;

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    if (this.isStandaloneApp) {
      this.provider = PROVIDERS.EXTERNAL;
      this.step = PROVISIONING_STEPS.LINE_CONFIG;
    }
  }

  get isStandaloneApp() {
    return this.appService.app.canUseStandalone;
  }

  get canGoBack() {
    if (this.isStandaloneApp) {
      return this.step !== PROVISIONING_STEPS.LINE_CONFIG;
    }
    return this.step !== PROVISIONING_STEPS.PROVIDER_SELECTION;
  }

  get STEPS() {
    return PROVISIONING_STEPS;
  }

  get PROVIDER_TYPES() {
    return PROVIDERS;
  }

  get INCOMING_CALL_TYPES() {
    return INCOMING_TYPES;
  }

  get selectedPhoneNumberFormatted() {
    return parsePhoneNumberFromString(this.selectedPhoneNumber)?.formatInternational() || '';
  }

  get selectedCountryFlag() {
    return PHONE_NUMBER_COUNTRY_DETAILS.get('US')?.flag ?? '';
  }

  get hasAvailablePhoneNumbers() {
    return this.availablePhoneNumbers.length > 0;
  }

  get canPurchaseNumber() {
    return !!this.selectedPhoneNumber && !this.isLoading;
  }

  private get phoneNumberRequestParams() {
    return {
      app_id: this.appService.app.id,
      country: 'US',
      phone_type: PhoneNumberType.Local,
    };
  }

  get isPurchasingNumber() {
    return taskFor(this.purchasePhoneNumber).isRunning;
  }

  @action
  async createNewLine() {
    if (this.incomingType === INCOMING_TYPES.PSTN) {
      await taskFor(this.fetchAvailablePhoneNumbers).perform();
      await this.purchaseNumber();
    } else {
      await taskFor(this.createSipLine).perform();
    }
  }

  @action
  setProvider(provider: Provider) {
    this.provider = provider;
    this.isDirty = true;
  }

  @action
  setIncomingType(incomingType: IncomingType) {
    this.incomingType = incomingType;
    this.isDirty = true;
  }

  @action
  setLineName(value: string) {
    this.lineName = value;
    this.isDirty = true;
  }

  @action
  nextStep() {
    if (this.provider === PROVIDERS.INTERCOM) {
      this.thirdPartyProvider = PhoneNumberProvider.Intercom;
      this.goToIntercomProvisioning();
    } else {
      this.step = PROVISIONING_STEPS.LINE_CONFIG;
    }
  }

  @action
  previousStep() {
    if (this.isStandaloneApp) {
      this.step = PROVISIONING_STEPS.LINE_CONFIG;
    } else if (
      this.step === PROVISIONING_STEPS.INTERCOM_PROVISIONING ||
      this.step === PROVISIONING_STEPS.LINE_CONFIG
    ) {
      this.step = PROVISIONING_STEPS.PROVIDER_SELECTION;
    }
  }

  @action
  purchaseNumberStep() {
    this.goToIntercomProvisioning();
  }

  private goToIntercomProvisioning() {
    this.step = PROVISIONING_STEPS.INTERCOM_PROVISIONING;
    this.initializePhoneNumberProvisioning();
  }

  @action
  async initializePhoneNumberProvisioning() {
    try {
      this.isLoading = true;
      await taskFor(this.fetchAvailablePhoneNumbers).perform();
      await this.fetchPricingInfo();
    } catch (error) {
      this.handleApiError(error, 'operator.fin.setup.phone-purchase.fetch-error');
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async purchaseNumber() {
    if (!this.selectedPhoneNumber) {
      this.handleApiError(null, 'operator.fin.setup.phone-purchase.no-numbers-available');
      return;
    }
    await taskFor(this.purchasePhoneNumber).perform();
  }

  @dropTask
  *fetchAvailablePhoneNumbers(): TaskGenerator<void> {
    try {
      this.availablePhoneNumbers = yield get(
        `/ember/calling_settings/fetch_available_phone_numbers`,
        {
          ...this.phoneNumberRequestParams,
          start_with: '',
        },
      ) as string[];

      if (this.hasAvailablePhoneNumbers) {
        this.selectedPhoneNumber = this.availablePhoneNumbers[0] || '';
      } else {
        this.handleApiError(null, 'operator.fin.setup.phone-purchase.no-numbers-available');
      }
    } catch (error) {
      this.handleApiError(error, 'operator.fin.setup.phone-purchase.fetch-error');
    }
  }

  @dropTask
  *purchasePhoneNumber(): TaskGenerator<void> {
    try {
      let response: CallingPhoneNumber = yield post(
        `/ember/calling_settings/provision_phone_number`,
        {
          ...this.phoneNumberRequestParams,
          selected_phone_number: this.selectedPhoneNumber,
        },
      );

      yield this.configurePhoneNumber(response);

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.phone-purchase.success', {
          phoneNumber: this.selectedPhoneNumberFormatted,
        }),
      );

      this.handleLineCreated(response.id);
    } catch (error: any) {
      this.handleApiError(error, 'operator.fin.setup.phone-purchase.error');
      this.selectedPhoneNumber = '';
      throw error;
    }
  }

  @dropTask
  *createSipLine(): TaskGenerator<void> {
    try {
      yield post(`/ember/fin_voice/sip_domain_configurations`, {
        app_id: this.appService.app.id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.sip-line-purchase.success', {
          phoneNumber: this.selectedPhoneNumberFormatted,
        }),
      );
      this.handleLineCreated();
    } catch (error) {
      this.handleApiError(error, 'operator.fin.setup.sip-line-purchase.error');
      throw error;
    }
  }

  private async configurePhoneNumber(response: CallingPhoneNumber): Promise<void> {
    this.store.pushPayload({ 'calling-phone-number': response });
    let phoneNumber = this.store.peekRecord('calling-phone-number', response.id);

    if (phoneNumber) {
      phoneNumber.inboundEnabled = true;
      phoneNumber.routingMethod = 'workflow';
      phoneNumber.externalProvider = this.thirdPartyProvider;
      phoneNumber.name =
        this.lineName || this.intl.t('ai-agent.voice-playground.provisioning.untitled-line');
      await phoneNumber.save();
    }

    this.finVoiceConfigService.numberConfigurationsLoaded = false;
  }

  @dropTask
  *fetchPrice(): TaskGenerator<PhonePricingResponse> {
    return yield get(
      `/ember/calling_settings/fetch_phone_number_price`,
      this.phoneNumberRequestParams,
    ) as PhonePricingResponse;
  }

  @action
  async fetchPricingInfo() {
    try {
      let response = await taskFor(this.fetchPrice).perform();
      this.selectedPhoneNumberPrice = response.price;
    } catch (error) {
      this.handleApiError(error, 'operator.fin.setup.phone-purchase.fetch-error');
    }
  }

  private handleApiError(error: any, defaultMessageKey: string): string {
    let errorMessage = error?.jqXHR?.responseJSON?.errors || this.intl.t(defaultMessageKey);
    this.notificationsService.notifyError(errorMessage);
    return errorMessage;
  }

  @action
  handleModalClose() {
    if (this.isDirty) {
      this.showConfirmationModal = true;
      return;
    }
    this.resetAttributes();
    this.args.onCloseModal();
  }

  @action
  handleLineCreated(phoneNumberId?: string) {
    this.finVoiceConfigService.numberConfigurationsLoaded = false;
    this.resetAttributes();
    this.args.onCloseModal();
    if (phoneNumberId && this.args.onLineCreated) {
      this.args.onLineCreated(phoneNumberId);
    } else {
      this.finVoiceConfigService.loadAllData();
    }
  }

  @action
  confirmCloseModal() {
    this.showConfirmationModal = false;
    this.resetAttributes();
    this.args.onCloseModal();
  }

  @action
  cancelCloseModal() {
    this.showConfirmationModal = false;
  }

  @action
  resetAttributes() {
    this.provider = this.isStandaloneApp ? PROVIDERS.EXTERNAL : PROVIDERS.INTERCOM;
    this.lineName = '';
    this.incomingType = INCOMING_TYPES.PSTN;
    this.thirdPartyProvider = PhoneNumberProvider.Other;
    this.step = this.isStandaloneApp
      ? PROVISIONING_STEPS.LINE_CONFIG
      : PROVISIONING_STEPS.PROVIDER_SELECTION;
    this.isDirty = false;
    this.showConfirmationModal = false;
    this.selectedPhoneNumber = '';
    this.selectedPhoneNumberPrice = 0;
    this.availablePhoneNumbers = [];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::LineProvisioning': typeof LineProvisioning;
  }
}
